import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Plus, Pencil, Trash2, AlertTriangle } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import TemplateFormDialog, { labelText } from "@/components/templates/TemplateFormDialog";
import { CONVERSATIONS } from "@/constants/testIds";

// Fix AC (2026-10-15): plantillas de WhatsApp desde la plataforma, sin entrar a Meta.
const STATUS = {
  APPROVED: ["Aprobada", "bg-[#E3EDE1] text-[#5B7A54]"],
  PENDING: ["En revisión", "bg-[#F4E4D6] text-[#B5793A]"],
  IN_APPEAL: ["En apelación", "bg-[#F4E4D6] text-[#B5793A]"],
  REJECTED: ["Rechazada", "bg-[#F6DEDC] text-[#A4473C]"],
  PAUSED: ["Pausada", "bg-[#F6DEDC] text-[#A4473C]"],
  DISABLED: ["Desactivada", "bg-muted text-muted-foreground"],
};
const REASONS = {
  INVALID_FORMAT: "El formato no es válido: revisá el texto y los datos.",
  ABUSIVE_CONTENT: "Meta consideró que el contenido no está permitido.",
  PROMOTIONAL: "Meta lo consideró una promoción.",
  TAG_CONTENT_MISMATCH: "El contenido no coincide con la categoría.",
  INCORRECT_CATEGORY: "El contenido no coincide con la categoría.",
  SCAM: "Meta lo consideró engañoso.",
};

export default function TemplatesPage() {
  const [items, setItems] = useState(null);
  const [error, setError] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const { toast } = useToast();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/whatsapp/templates");
      setItems(data.items); setError("");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "No se pudieron cargar las plantillas");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (values) => {
    try {
      if (editing) await api.put(`/whatsapp/templates/${editing.id}`, values);
      else await api.post("/whatsapp/templates", values);
      toast({ title: editing ? "Plantilla enviada a revisión" : "Plantilla creada", description: "Meta la revisa antes de que se pueda usar." });
      setFormOpen(false); setEditing(null); load();
      return true;
    } catch (err) {
      toast({ title: "No se pudo guardar", description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" });
      return false;
    }
  };

  const remove = async () => {
    const target = deleting; setDeleting(null);
    try {
      await api.delete(`/whatsapp/templates/${target.id}`);
      toast({ title: "Plantilla borrada" }); load();
    } catch (err) {
      toast({ title: "No se pudo borrar", description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" });
    }
  };

  return (
    <div className="max-w-4xl" data-testid={CONVERSATIONS.templatesPage}>
      <Link to="/conversaciones" className="text-sm text-muted-foreground inline-flex items-center gap-1 mb-3 hover:text-foreground"><ArrowLeft className="w-4 h-4" /> Conversaciones</Link>
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div>
          <h1 className="font-serif text-3xl sm:text-4xl tracking-tight text-foreground">Plantillas de WhatsApp</h1>
          <p className="text-base text-muted-foreground mt-1">Mensajes para escribirle a un cliente después de 24 horas. Meta las revisa antes de poder usarlas.</p>
        </div>
        <Button onClick={() => { setEditing(null); setFormOpen(true); }} className="bg-brand hover:bg-brand-hover text-white gap-1.5" data-testid={CONVERSATIONS.templateNewButton}>
          <Plus className="w-4 h-4" /> Nueva plantilla
        </Button>
      </div>
      {/* Fix AI (2026-10-23): un error largo se corta en vez de salirse de la pantalla */}
      {error && <p className="text-sm text-destructive mb-4 break-words">{error}</p>}
      {items === null && !error && <p className="text-sm text-muted-foreground">Cargando...</p>}
      {items && items.length === 0 && <p className="text-sm text-muted-foreground">Todavía no hay plantillas.</p>}
      <div className="space-y-3">
        {(items || []).map((t) => {
          const [label, color] = STATUS[t.status] || [t.status, "bg-muted text-muted-foreground"];
          return (
            <div key={t.id} className="bg-white border border-border rounded-xl p-4" data-testid={`${CONVERSATIONS.templateRowPrefix}${t.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-medium text-foreground">{t.name.replaceAll("_", " ")}</p>
                  <span className="text-xs text-muted-foreground uppercase">{t.language}</span>
                  <Badge className={`${color} border-none font-normal text-[11px]`}>{label}</Badge>
                </div>
                <div className="flex gap-1">
                  {t.editable && (
                    <Button variant="ghost" size="sm" className="gap-1" onClick={() => { setEditing(t); setFormOpen(true); }} data-testid={`${CONVERSATIONS.templateEditPrefix}${t.id}`}><Pencil className="w-3.5 h-3.5" /> Editar</Button>
                  )}
                  <Button variant="ghost" size="sm" className="gap-1 text-destructive hover:text-destructive" onClick={() => setDeleting(t)} data-testid={`${CONVERSATIONS.templateDeletePrefix}${t.id}`}><Trash2 className="w-3.5 h-3.5" /> Borrar</Button>
                </div>
              </div>
              {t.header && <p className="text-sm font-medium mt-2">{t.header}</p>}
              <p className="text-sm text-muted-foreground whitespace-pre-wrap mt-2">{labelText(t.body, t.footer)}</p>
              {t.buttons.length > 0 && <div className="flex flex-wrap gap-1.5 mt-2">{t.buttons.map((b, i) => <span key={i} className="text-xs border border-border rounded-full px-2.5 py-1">{b}</span>)}</div>}
              {t.status === "REJECTED" && (
                <p className="text-xs text-[#A4473C] mt-2">Motivo: {REASONS[t.rejected_reason] || t.rejected_reason || "Meta no indicó el motivo."} {t.editable ? "Podés editarla y mandarla de nuevo." : ""}</p>
              )}
              {t.category === "MARKETING" && (
                <p className="text-xs text-[#B5793A] mt-2 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> Meta la clasificó como promoción: no llega a números de Estados Unidos.</p>
              )}
              {!t.usable && <p className="text-xs text-muted-foreground mt-2">Esta plantilla no se puede mandar desde la plataforma (tiene imagen o datos en el encabezado o los botones).</p>}
            </div>
          );
        })}
      </div>

      <TemplateFormDialog open={formOpen} onOpenChange={(o) => { setFormOpen(o); if (!o) setEditing(null); }} template={editing} onSubmit={save} />

      <AlertDialog open={Boolean(deleting)} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Borrar la plantilla {deleting?.name.replaceAll("_", " ")} ({deleting?.language})?</AlertDialogTitle>
            <AlertDialogDescription>Se borra de WhatsApp y no se puede deshacer. Si estaba aprobada, ese nombre no se puede volver a usar por 30 días.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-destructive hover:bg-destructive/90" data-testid={CONVERSATIONS.templateDeleteConfirm}>Borrar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
